import { useEffect, useState, useCallback, useRef } from "react"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { getRecentPatients, getUpcomingAppointments, searchPatients } from "@/lib/api"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@/context/AuthContext"
import AddAppointmentModal from "@/components/Appointment/AddAppointmentModal"
import {
  Search,
  User,
  Calendar,
  Clock,
  Phone,
  Mail,
  MapPin,
  Loader2,
  AlertCircle,
  ArrowRight,
  History,
  CalendarDays,
  UserPlus,
  FileText,
  Stethoscope,
  Plus
} from "lucide-react"
import { cn } from "@/lib/utils"
import { toast } from "@/hooks/use-toast"
import NewPatientModal from "@/components/Patient/NewPatientModal"

type SearchResult = {
  id: string
  type: 'patient' | 'appointment'
  name: string
  subtitle: string
  metadata?: string
  badge?: string
}

type Props = {
  open: boolean
  onClose: () => void
}

function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export default function SearchOverlay({ open, onClose }: Props) {
  const { currentUser } = useAuth()
  const isStaff = currentUser?.role === 'staff'
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [recentPatients, setRecentPatients] = useState<any[]>([])
  const [upcomingAppointments, setUpcomingAppointments] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [showNewPatientModal, setShowNewPatientModal] = useState(false)
  const [showAddAppointmentModal, setShowAddAppointmentModal] = useState(false)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)

  // Debounced search function
  const debouncedSearch = useCallback(
      debounce(async (searchQuery: string) => {
        if (searchQuery.trim().length === 0) {
          setResults([])
          return
        }

        setLoading(true)
        setError(null)

        try {
          const searchResults = await searchPatients(searchQuery, currentUser?.clinic_id)

          // Transform API results to SearchResult format and limit to 10 results
          const transformedResults: SearchResult[] = (searchResults || [])
          .slice(0, 10)
          .map((patient: any) => ({
            id: patient.id,
            type: 'patient',
            name: patient.name,
            subtitle: `${patient.age} years • ${patient.gender}`,
            metadata: patient.phone,
            badge: patient.lastVisit ? 'Recent' : undefined
          }))

          setResults(transformedResults)
          setSelectedIndex(-1)
        } catch (err) {
          console.error("Search failed:", err)
          setError("Search failed. Please check your connection and try again.")
          setResults([])
        } finally {
          setLoading(false)
        }
      }, 300),
      []
  )

  // Reset when closed
  useEffect(() => {
    if (!open) {
      setQuery("")
      setResults([])
      setError(null)
      setSelectedIndex(-1)
    } else {
      // Focus input when opened
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  // Trigger search when query changes
  useEffect(() => {
    debouncedSearch(query)
  }, [query, debouncedSearch])

  // Load default suggestions
  useEffect(() => {
    const loadDefaults = async () => {
      if (!open || !currentUser?.clinic_id) return

      try {
        const [patientsRes, appointmentsRes] = await Promise.all([
          getRecentPatients(5, currentUser.doctor_id ?? undefined, currentUser.clinic_id),
          getUpcomingAppointments(5, currentUser.doctor_id ?? undefined, currentUser.clinic_id)
        ])
        
        setRecentPatients(Array.isArray(patientsRes) ? patientsRes : [])
        setUpcomingAppointments(appointmentsRes || [])
      } catch (e) {
        console.error("Failed to load defaults:", e)
      }
    }

    loadDefaults()
  }, [open, currentUser?.doctor_id, currentUser?.clinic_id])

  // Reset selected index when content changes
  useEffect(() => {
    setSelectedIndex(-1)
  }, [results.length, recentPatients.length, upcomingAppointments.length])

  // Handle selection function
  const handleSelection = useCallback((index: number) => {
    if (results.length > 0) {
      const result = results[index]
      if (result?.type === 'patient') {
        navigate(isStaff ? `/frontdesk/patients/${result.id}` : `/patients/${result.id}`)
      }
    } else {
      const allDefaults = [...recentPatients, ...upcomingAppointments]
      const item = allDefaults[index]
      if (item) {
        const patientId = item.patient_id || item.id
        navigate(isStaff ? `/frontdesk/patients/${patientId}` : `/patients/${patientId}`)
      }
    }
    onClose()
  }, [results, recentPatients, upcomingAppointments, navigate, onClose, isStaff])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return

      // Calculate total items correctly based on current state
      const totalItems = results.length > 0
        ? results.length
        : (recentPatients.length + upcomingAppointments.length)

      // Don't handle navigation if there are no items
      if (totalItems === 0) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex(prev => (prev + 1) % totalItems)
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex(prev => prev <= 0 ? totalItems - 1 : prev - 1)
          break
        case 'Enter':
          e.preventDefault()
          if (selectedIndex >= 0 && selectedIndex < totalItems) {
            handleSelection(selectedIndex)
          }
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, results.length, recentPatients.length, upcomingAppointments.length, selectedIndex, onClose, handleSelection])

  const goToPatient = (id: string) => {
    navigate(isStaff ? `/frontdesk/patients/${id}` : `/patients/${id}`)
    onClose()
  }

  const handleNewPatient = () => {
    onClose();
    setShowNewPatientModal(true);
  }

  const handleAddAppointment = () => {
    onClose();
    setShowAddAppointmentModal(true);
  }

  const QuickActionButton = ({ icon: Icon, label, action, variant = "outline" }: {
    icon: any,
    label: string,
    action: () => void,
    variant?: "outline" | "default"
  }) => (
      <Button
          variant={variant}
          size="sm"
          onClick={action}
          className="flex items-center gap-2 h-9"
      >
        <Icon className="w-4 h-4" />
        {label}
      </Button>
  )

  const SearchResultItem = ({ result, index, isSelected }: {
    result: SearchResult,
    index: number,
    isSelected: boolean
  }) => (
      <div
          onClick={() => handleSelection(index)}
          className={cn(
              "flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all group border",
              isSelected
                  ? "bg-primary/5 border-primary/20"
                  : "hover:bg-muted/50 border-transparent"
          )}
      >
        <div className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center transition-colors",
            result.type === 'patient'
                ? "bg-info/10 text-info"
                : "bg-success/10 text-success"
        )}>
          {result.type === 'patient' ? (
              <User className="w-4 h-4" />
          ) : (
              <Calendar className="w-4 h-4" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-foreground truncate text-sm">
              {result.name}
            </h4>
            {result.badge && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  {result.badge}
                </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {result.subtitle}
          </p>
          {result.metadata && (
              <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                <Phone className="w-2.5 h-2.5" />
                {result.metadata}
              </p>
          )}
        </div>

        <ArrowRight className={cn(
            "w-3.5 h-3.5 text-muted-foreground transition-all",
            isSelected ? "text-primary" : "group-hover:text-foreground"
        )} />
      </div>
  )

  const EmptyState = ({ icon: Icon, title, description, action }: {
    icon: any,
    title: string,
    description: string,
    action?: { label: string, onClick: () => void }
  }) => (
      <div className="flex flex-col items-center justify-center h-full py-6 text-center">
        <div className="w-10 h-10 rounded-full bg-muted/30 flex items-center justify-center mb-2.5">
          <Icon className="w-5 h-5 text-muted-foreground" />
        </div>
        <h3 className="font-medium text-sm text-foreground mb-1">{title}</h3>
        <p className="text-xs text-muted-foreground mb-3 max-w-[180px]">{description}</p>
        {action && (
            <QuickActionButton
                icon={UserPlus}
                label={action.label}
                action={action.onClick}
                variant="default"
            />
        )}
      </div>
  )

  return (
      <>
        <Dialog open={open} onOpenChange={onClose}>
          <DialogContent className="max-w-3xl p-0 gap-0 h-[75vh] overflow-hidden flex flex-col">
          {/* Header - Following EditAppointmentModal theme */}
          <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-4 pb-3 border-b border-border flex-shrink-0">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <Search className="w-5 h-5 text-primary" />
                </div>
                  <div>
                    <h2 className="text-base font-semibold text-foreground">Search</h2>
                    <p className="text-xs text-muted-foreground">
                      Find patients, appointments, and more
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                        id="search-overlay-input"
                        name="search-overlay-input"
                        ref={inputRef}
                        placeholder="Search patients by name or phone..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="w-full pl-8 pr-4 h-9 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                    {loading && (
                        <Loader2 className="absolute right-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {error && (
                  <div className="p-4">
                    <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-sm">Search Error</p>
                        <p className="text-xs opacity-90">{error}</p>
                      </div>
                    </div>
                  </div>
              )}

              {/* Search Results */}
              {query.trim().length > 0 && !error && (
                  <div className="p-4">
                    {loading ? (
                        <div className="space-y-1">
                          {[...Array(3)].map((_, i) => (
                              <div key={i} className="flex items-center gap-2 p-1.5">
                                <div className="w-8 h-8 bg-muted rounded-full animate-pulse" />
                                <div className="flex-1 space-y-1">
                                  <div className="h-3 bg-muted rounded animate-pulse w-3/4" />
                                  <div className="h-2 bg-muted rounded animate-pulse w-1/2" />
                                </div>
                              </div>
                          ))}
                        </div>
                    ) : results.length === 0 ? (
                        <EmptyState
                            icon={Search}
                            title="No results found"
                            description="Try adjusting your search terms or create a new patient"
                            action={{
                              label: "Add New Patient",
                              onClick: handleNewPatient
                            }}
                        />
                    ) : (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between mb-1.5">
                            <h3 className="text-xs font-medium text-muted-foreground">
                              Search Results ({results.length})
                            </h3>
                            {results.length >= 10 && (
                                <span className="text-[10px] text-muted-foreground">
                          Showing first 10 results
                        </span>
                            )}
                          </div>
                    <div className="space-y-2">
                          {results.map((result, index) => (
                              <SearchResultItem
                                  key={result.id}
                                  result={result}
                                  index={index}
                                  isSelected={selectedIndex === index}
                              />
                          ))}
                        </div>
                  </div>
                    )}
                  </div>
              )}

              {/* Default View - Split into two sections */}
              {query.trim().length === 0 && !error && (
                  <div className="p-4 h-full">
                    <div className="grid grid-cols-2 gap-4 h-full">
                      {/* Upcoming Appointments Section */}
                      <div className="space-y-1.5 h-full flex flex-col">
                        <h3 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                          <CalendarDays className="w-3.5 h-3.5" />
                          Upcoming Appointments
                        </h3>
                        <div className="space-y-1 flex-1 overflow-y-auto pr-2">
                          {upcomingAppointments.length > 0 ? (
                              upcomingAppointments.map((appt, index) => (
                                  <div
                                      key={appt.id}
                                      onClick={() => goToPatient(appt.patient_id)}
                                      className={cn(
                              "flex items-center gap-2 p-1.5 rounded-lg cursor-pointer transition-all group",
                                          selectedIndex === index + results.length
                                              ? "bg-primary/5 border-primary/20"
                                              : "hover:bg-muted/50 border-transparent"
                                      )}
                                  >
                            <div className="w-8 h-8 bg-medical-schedule/10 text-medical-schedule rounded-full flex items-center justify-center">
                                      <Calendar className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-medium text-sm text-foreground truncate">
                                        {appt.patient_name}
                                      </h4>
                                      <p className="text-xs text-muted-foreground">
                                        {new Date(appt.date).toLocaleDateString('en-IN', {
                                          day: 'numeric', month: 'short'
                                        })} at {appt.time
                                          ? (() => {
                                              const [h, m] = appt.time.split(':').map(Number);
                                              const d = new Date(); d.setHours(h, m);
                                              return d.toLocaleTimeString('en-US', {
                                                hour: 'numeric', minute: '2-digit', hour12: true
                                              });
                                            })()
                                          : appt.time
                                        }
                                      </p>
                                      {appt.reason && (
                                          <p className="text-[10px] text-muted-foreground mt-0.5">
                                            {appt.reason}
                                          </p>
                                      )}
                                    </div>
                                    <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
                                  </div>
                              ))
                          ) : (
                              <EmptyState
                                  icon={Calendar}
                                  title="No upcoming appointments"
                                  description="Schedule new appointments for your patients"
                                  action={{
                                    label: "New Appointment",
                                    onClick: handleAddAppointment
                                  }}
                              />
                          )}
                        </div>
                      </div>

                      {/* Recent Patients Section */}
                      <div className="space-y-1.5 h-full flex flex-col">
                        <h3 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                          <History className="w-3.5 h-3.5" />
                          Recent Patients
                        </h3>
                        <div className="space-y-1 flex-1 overflow-y-auto pr-2">
                          {recentPatients.length > 0 ? (
                              recentPatients.map((patient, index) => (
                                  <div
                                      key={patient.id}
                                      onClick={() => goToPatient(patient.id)}
                                      className={cn(
                                          "flex items-center gap-2 p-1.5 rounded-lg cursor-pointer transition-all group border",
                                          selectedIndex === index + results.length + upcomingAppointments.length
                                              ? "bg-primary/5 border-primary/20"
                                              : "hover:bg-muted/50 border-transparent"
                                      )}
                                  >
                                    <div className="w-8 h-8 bg-info/10 text-info rounded-full flex items-center justify-center">
                                      <User className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-medium text-sm text-foreground truncate">
                                        {patient.name}
                                      </h4>
                                      <p className="text-xs text-muted-foreground">
                                        {patient.age} years • {patient.gender}
                                      </p>
                                      {patient.phone && (
                                          <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                                            <Phone className="w-2.5 h-2.5" />
                                            {patient.phone}
                                          </p>
                                      )}
                                    </div>
                                    <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
                                  </div>
                              ))
                          ) : (
                              <EmptyState
                                  icon={User}
                                  title="No recent patients"
                                  description="Add new patients to your practice"
                                  action={{
                                    label: "New Patient",
                                    onClick: handleNewPatient
                                  }}
                              />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-2.5 border-t bg-muted/30 flex-shrink-0">
              <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 bg-background border rounded text-[9px]">↑↓</kbd>
                  Navigate
                </span>
                  <span className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 bg-background border rounded text-[9px]">↵</kbd>
                  Select
                </span>
                  <span className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 bg-background border rounded text-[9px]">Esc</kbd>
                  Close
                </span>
                </div>
                <span>⌘K to search anywhere</span>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <NewPatientModal
            isOpen={showNewPatientModal}
            onClose={() => setShowNewPatientModal(false)}
            onSuccess={() => setShowNewPatientModal(false)}
            title="Add New Patient"
            showPrescriptionOption={!isStaff}
            showQueueOption={false}
        />

        <AddAppointmentModal
            isOpen={showAddAppointmentModal}
            onClose={() => setShowAddAppointmentModal(false)}
            patients={recentPatients}
            onAdded={() => {
              setShowAddAppointmentModal(false);
              // Show success message
              toast({
                title: "Appointment Created",
                description: "New appointment has been scheduled successfully.",
                variant: "default"
              });
            }}
            showPatientSelection={true}
            title="New Appointment"
            description="Schedule a new patient appointment"
        />
      </>
  )
}
